import { PlausibleEvent } from 'enums/plausible'
import { useEffect } from 'react'
import { PrivacyPolicy } from 'static/regulation'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import PageLayout from 'layouts/PageLayout'
import { getTitleLocation, MetaTypeNames } from 'pages'

import Markdown from 'components/Markdown/Markdown'
import PageSelector from 'components/PageSelector/PageSelector'
import StructuredData from 'components/StructuredData/StructuredData'
import useIsQVariant from 'utils/hooks/useIsQVariant'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const WhatIsBinany = ({
  referrer,
  ssrHost,
}: {
  referrer: string
  ssrHost: string
}) => {
  const { t } = useTranslation(['regulation', 'seo', 'shared', 'contacts', 'main'])
  const setEvent = usePlausible()

  const isQ = useIsQVariant()

  useEffect(() => {
    setEvent(PlausibleEvent.OpenedPrivacy, {
      step: 1,
      referrer,
    })
  }, [])

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }

  return (
    <>
      <StructuredData
        host={ssrHost}
        variant="reduced"
        path="/regulation/privacy-policy"
        pageTitle={t(`seo:${getTitleLocation(ssrHost, 'privacy', MetaTypeNames.Title)}`)}
      />
      <Head>
        {/* t(`seo:privacyTitle`) */}
        {/* t(`seo:privacyTitle24`) */}
        <title>{t(`seo:${getTitleLocation(ssrHost, 'privacy', MetaTypeNames.Title)}`)}</title>
        {/* t(`seo:privacyDescription`) */}
        {/* t(`seo:privacyDescription24`) */}
        <meta
          name="description"
          content={t(`seo:${getTitleLocation(ssrHost, 'privacy', MetaTypeNames.Description)}`)}
        />

        <meta
          property="og:title"
          content={t(`seo:${getTitleLocation(ssrHost, 'privacy', MetaTypeNames.Title)}`)}
        />
        <meta
          property="og:description"
          content={t(`seo:${getTitleLocation(ssrHost, 'privacy', MetaTypeNames.Description)}`)}
        />
        <meta
          property="og:image"
          content={`https://${ssrHost}/images/ogImages/regulationPrivacy.png`}
        />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://${ssrHost}/regulation/privacy-policy`} />
      </Head>
      <PageLayout
        subtitle={t('main:whatisWeWillTellYou')}
        title={t('shared:navigationPrivacy')}
        breadcrumbs={[
          { title: t('main:binanyTrading'), link: '/' },
          { title: t('shared:navigationPrivacy'), link: '/regulation/privacy-policy' },
        ]}
        host={ssrHost}
      >
        <PageSelector
          pages={[
            {
              title: t('shared:navigationWhatis'),
              link: isQ ? '/q/regulation/whatis' : '/regulation/whatis',
              name: 'whatis',
            },
            {
              title: t('shared:navigationPrivacy'),
              link: isQ ? '/q/regulation/privacy-policy' : '/regulation/privacy-policy',
              name: 'privacy',
            },
            {
              title: t('shared:navigationTerms'),
              link: isQ ? '/q/regulation/terms-and-conditions' : '/regulation/terms-and-conditions',
              name: 'agreement',
            },
          ]}
          pageName="privacy"
          referrer={referrer}
          selectedPage={{
            title: t('shared:navigationPrivacy'),
            link: isQ ? '/q/regulation/privacy-policy' : '/regulation/privacy-policy',
            name: 'privacy',
          }}
        />
        <Markdown dir="ltr" unnumerableList scrollable={false}>
          {PrivacyPolicy}
        </Markdown>
      </PageLayout>
    </>
  )
}

export default WhatIsBinany

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      ssrHost: req.headers.host || '',
      ...(await serverSideTranslations(locale, [
        'regulation',
        'seo',
        'shared',
        'contacts',
        'main',
      ])),
    },
  }
}
